// Static ISO 3166-1 alpha-2 groupings used by the Geo editor for region
// quick-add buttons and for grouping selected countries by continent.
//
// This is purely a presentation aid. The backend contract is unchanged: geo
// whitelist/blacklist are still plain arrays of country codes. Region buttons
// only ever add/remove codes that the backend actually offers (we intersect
// these lists with the available geo-options), so any code listed here that the
// backend doesn't support is simply ignored, and any backend code missing from
// these lists still works via search and shows under "Other".

// Continents are the source of truth for grouping (each country -> one
// continent). North & South America are intentionally combined into "Americas".
export const CONTINENTS = {
  europe: {
    label: "Europe",
    codes: [
      "AL", "AD", "AT", "BY", "BE", "BA", "BG", "HR", "CY", "CZ", "DK", "EE",
      "FO", "FI", "FR", "DE", "GI", "GR", "GG", "HU", "IS", "IE", "IM", "IT",
      "JE", "XK", "LV", "LI", "LT", "LU", "MT", "MD", "MC", "ME", "NL", "MK",
      "NO", "PL", "PT", "RO", "RU", "SM", "RS", "SK", "SI", "ES", "SE", "CH",
      "UA", "GB", "VA"
    ]
  },
  americas: {
    label: "Americas",
    codes: [
      "AI", "AG", "AR", "AW", "BS", "BB", "BZ", "BM", "BO", "BQ", "BR", "CA",
      "KY", "CL", "CO", "CR", "CU", "CW", "DM", "DO", "EC", "SV", "FK", "GF",
      "GL", "GD", "GP", "GT", "GY", "HT", "HN", "JM", "MQ", "MX", "MS", "NI",
      "PA", "PY", "PE", "PR", "BL", "KN", "LC", "MF", "PM", "VC", "SR", "TT",
      "TC", "US", "UY", "VE", "VG", "VI"
    ]
  },
  asia: {
    label: "Asia",
    codes: [
      "AF", "AM", "AZ", "BH", "BD", "BT", "BN", "KH", "CN", "GE", "HK", "IN",
      "ID", "IR", "IQ", "IL", "JP", "JO", "KZ", "KW", "KG", "LA", "LB", "MO",
      "MY", "MV", "MN", "MM", "NP", "KP", "OM", "PK", "PS", "PH", "QA", "SA",
      "SG", "KR", "LK", "SY", "TW", "TJ", "TH", "TL", "TR", "TM", "AE", "UZ",
      "VN", "YE"
    ]
  },
  africa: {
    label: "Africa",
    codes: [
      "DZ", "AO", "BJ", "BW", "BF", "BI", "CV", "CM", "CF", "TD", "KM", "CG",
      "CD", "CI", "DJ", "EG", "GQ", "ER", "SZ", "ET", "GA", "GM", "GH", "GN",
      "GW", "KE", "LS", "LR", "LY", "MG", "MW", "ML", "MR", "MU", "YT", "MA",
      "MZ", "NA", "NE", "NG", "RE", "RW", "SH", "ST", "SN", "SC", "SL", "SO",
      "ZA", "SS", "SD", "TZ", "TG", "TN", "UG", "EH", "ZM", "ZW"
    ]
  },
  oceania: {
    label: "Oceania",
    codes: [
      "AS", "AU", "CK", "FJ", "PF", "GU", "KI", "MH", "FM", "NR", "NC", "NZ",
      "NU", "NF", "MP", "PW", "PG", "PN", "WS", "SB", "TK", "TO", "TV", "VU",
      "WF"
    ]
  }
}

// Order in which continent groups are displayed.
export const CONTINENT_ORDER = ["europe", "americas", "asia", "africa", "oceania"]

// EU member states (27).
const EU_CODES = [
  "AT", "BE", "BG", "HR", "CY", "CZ", "DK", "EE", "FI", "FR", "DE", "GR", "HU",
  "IE", "IT", "LV", "LT", "LU", "MT", "NL", "PL", "PT", "RO", "SK", "SI", "ES",
  "SE"
]

// EEA = EU + Iceland, Liechtenstein, Norway.
const EEA_CODES = EU_CODES.concat(["IS", "LI", "NO"])

// Political unions shown as quick-add buttons alongside the continents. These
// intentionally overlap the continents (e.g. EU countries are also in Europe).
export const POLITICAL = {
  EU: { label: "EU", codes: EU_CODES },
  EEA: { label: "EEA", codes: EEA_CODES }
}

// Reverse lookup: country code -> continent key. Built from CONTINENTS so the
// continent lists stay the single source of truth.
export const CONTINENT_BY_CODE = Object.entries(CONTINENTS).reduce(
  (acc, [key, group]) => {
    group.codes.forEach(code => { acc[code] = key })
    return acc
  },
  {}
)
